import { type MouseEvent, useLayoutEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { NavLink, useLocation } from 'react-router-dom';
import logo from '../../assets/images/logo160.png';
import bannerData from './navigationBar-banner.json';
import './navigationBar.scss';
import { NavDropdown } from 'react-bootstrap';

type NavigationBanner = {
  id: string;
  active: boolean;
  type: 'alert' | 'warning' | 'notification';
  message: string;
  shortMessage?: string;
};

type NavigationBannerData = {
  bannerUpdates: NavigationBanner[];
};

function NavigationBar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [dismissedBanners, setDismissedBanners] = useState<string[]>([]);
  const { pathname } = useLocation();
  const visibleBanners = (bannerData as NavigationBannerData).bannerUpdates
    .map((banner, index) => ({ banner, key: banner.id || `banner-${index}` }))
    .filter(({ banner, key }) => banner.active && banner.message.trim() && !dismissedBanners.includes(key))
    .slice(0, 2);
  const scrollToTopIfCurrentPage = (destination: string, event: MouseEvent) => {
    const isCurrentHomePage = destination === '/' && pathname === '/home';
    if (pathname !== destination && !isCurrentHomePage) {
      return;
    }

    event.preventDefault();
    document.querySelector('.page-viewport')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useLayoutEffect(() => {
    const scrollContainer = document.querySelector<HTMLElement>('.page-viewport');
    const navigationBar = document.querySelector<HTMLElement>('.navigation-bar');
    const banners = document.querySelector<HTMLElement>('.navigation-bar-banners');
    const updateNavigationOffset = () => {
      const navigationBarHeight = navigationBar?.offsetHeight ?? 0;
      const bannerHeight = banners?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        '--navigation-bar-height',
        `${navigationBarHeight}px`
      );
      document.documentElement.style.setProperty(
        '--navigation-offset',
        `${navigationBarHeight + bannerHeight}px`
      );
    };
    const handleScroll = () => {
      setIsScrolled((scrollContainer?.scrollTop ?? window.scrollY) > 10);
    };
    handleScroll();
    updateNavigationOffset();
    scrollContainer?.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateNavigationOffset);
    const resizeObserver = new ResizeObserver(updateNavigationOffset);
    if (navigationBar) {
      resizeObserver.observe(navigationBar);
    }
    return () => {
      scrollContainer?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateNavigationOffset);
      resizeObserver.disconnect();
    };
  }, [pathname, visibleBanners.length]);

  return (
    <div
      className={`navigation-bar-shell ${visibleBanners.length ? 'navigation-bar-shell--has-banner' : ''} ${
        visibleBanners.length === 2 ? 'navigation-bar-shell--has-two-banners' : ''
      }`}
    >
      <Navbar expand="lg" className={`navigation-bar ${isScrolled ? 'navigation-bar--compact' : ''}`} fixed="top">
        <Container fluid className="navbar-container">
        
        {/* Left Layer: Brand Logo and Title */}
        <Navbar.Brand as={NavLink} to="/" className="navbar-brand" onClick={(event) => scrollToTopIfCurrentPage('/', event)}>
          <img src={logo} alt="MSIA Logo" className="navbar-logo" />
          <span className="navbar-brand-text">Missouri Self-Insurers Association</span>
        </Navbar.Brand>

        {/* Combined Mobile Toggle and Collapse Menu */}
        <Navbar.Toggle aria-controls="navbarScroll" className="navbar-toggler-custom" />
        
        <Navbar.Collapse id="navbarScroll" className="navbar-collapse">
          <Nav className="navbar-links">
            <Nav.Link as={NavLink} to="/" end onClick={(event) => scrollToTopIfCurrentPage('/', event)}> Home </Nav.Link>
            <NavDropdown title="About" id="basic-nav-dropdown">
              <NavDropdown.Item as={NavLink} to="/mission" onClick={(event) => scrollToTopIfCurrentPage('/mission', event)}>Mission</NavDropdown.Item>
              <NavDropdown.Item as={NavLink} to="/by-laws" onClick={(event) => scrollToTopIfCurrentPage('/by-laws', event)}>By-Laws</NavDropdown.Item>
              <NavDropdown.Item as={NavLink} to="/board-directory" onClick={(event) => scrollToTopIfCurrentPage('/board-directory', event)}>Board Directory</NavDropdown.Item>
            </NavDropdown>
            <Nav.Link as={NavLink} to="/schedule" onClick={(event) => scrollToTopIfCurrentPage('/schedule', event)}> Schedule </Nav.Link>
            <Nav.Link as={NavLink} to="/sponsors" onClick={(event) => scrollToTopIfCurrentPage('/sponsors', event)}> Sponsors </Nav.Link>
            <Nav.Link as={NavLink} to="/contact" onClick={(event) => scrollToTopIfCurrentPage('/contact', event)}> Contact </Nav.Link>
          </Nav>
        </Navbar.Collapse>
        </Container>
      </Navbar>

        {visibleBanners.length > 0 && (
          <div className="navigation-bar-banners">
            {visibleBanners.map(({ banner, key }) => (
              <div
                key={key}
                className={`navigation-bar-banner navigation-bar-banner--${banner.type}`}
                role="alert"
              >
                <span className="navigation-bar-banner__icon" aria-hidden="true">
                  {banner.type === 'alert' && '!'}
                  {banner.type === 'warning' && '⚠'}
                  {banner.type === 'notification' && '●'}
                </span>
                <span className="navigation-bar-banner__message">
                  <span className="navigation-bar-banner__message-full">{banner.message}</span>
                  {banner.shortMessage && (
                    <span className="navigation-bar-banner__message-short">{banner.shortMessage}</span>
                  )}
                </span>
                {banner.type !== 'alert' && (
                  <button
                    type="button"
                    className="navigation-bar-banner__dismiss"
                    onClick={() => setDismissedBanners((current) => [...current, key])}
                    aria-label={`Dismiss ${banner.type}`}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

export default NavigationBar;